import React, { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";

import { Block } from "components/Layout";
import Button from "components/Button";
import Input from "components/Form/Input";
import InputHoneypot from "components/Form/InputHoneypot";
import Textarea from "components/Form/Textarea";
import Checkbox from "../components/Form/Checkbox";
import DefaultErrorMessage from "../components/DefaultErrorMessage";
import { Flex } from "/components/Layout";
import { SUPPORTED_ZIP_CODES } from "../constants";

import postData from "utils/postData";
import { trackPixel } from "/utils/metaPixel";

import CartContext from "/context/cart";

import styles from "components/Form/Input.module.scss";
import containerStyles from "components/Locations/Locations.module.scss";
import parseCookies from "/utils/parseCookies";

type FormProps = {
  initialValues?: FormData;
  // eslint-disable-next-line no-unused-vars
  onFormSubmit: (b: boolean) => void;
  prevStep: () => void;
  customerHasValidCreditCard: boolean;
};

type FormData = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address: string;
  message: string;
  zipCode: string;
};

const OrderSykkelabo = ({
  initialValues,
  onFormSubmit,
  prevStep,
  customerHasValidCreditCard
}: FormProps) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [city, setCity] = useState("");
  const [userId, setUserId] = useState("");

  const cart = useContext(CartContext);
  const { updateForm, setStripeFormClientSecret } = cart;
  const zipCode = cart.state.form.zipCode;

  const { register, handleSubmit, errors, formState, clearErrors, setError } = useForm();

  useEffect(() => {
    const cookie = parseCookies(document.cookie);
    if (cookie?.uid) {
      setUserId(cookie.uid);
    }
  }, []);

  useEffect(() => {
    const getCity = async () => {
      const url = `https://ws.geonorge.no/adresser/v1/sok?postnummer=${zipCode}&treffPerSide=1&filtrer=adresser.poststed`;
      try {
        const response = await fetch(url);
        const result = await response.json();
        const { poststed } = result?.adresser[0] || {};
        const normalizedZip = poststed?.charAt(0) + poststed?.slice(1).toLowerCase();
        setCity(normalizedZip);
      } catch (error) {
        setCity("");
        console.error(error);
      }
      return null;
    };
    if (zipCode.length === 4) {
      getCity();
    } else {
      setCity("");
    }
  }, [zipCode]);

  useEffect(() => {
    if (zipCode.length === 4 && SUPPORTED_ZIP_CODES.includes(zipCode)) {
      setErrorMessage("");
      clearErrors("zipCode");
    }

    if (zipCode.length === 4 && !SUPPORTED_ZIP_CODES.includes(zipCode)) {
      setError("zipCode", {
        type: "focus",
        message: "Beklager, Whee! kan bare bestilles i Oslo."
      });
    }
  }, [zipCode]);

  const onSubmit = async (data: any) => {
    setErrorMessage("");

    if (cart.state.stripeFormClientSecret) {
      onFormSubmit(true); // Show credit card form
      return;
    }

    // Step 3: the order form is submitted ("Neste"/"Bestill nå"), so the user is
    // moving on to enter payment details — Meta's "about to pay" funnel step.
    trackPixel("AddPaymentInfo", { content_name: cart.state.bike?.cms?.title });

    try {
      const subscriptionsId = [
        cart.state.subscription.id,
        ...cart.state.accessories.map((a) => a.id)
      ];

      const subscriptions = [
        {
          // Bike subscription
          id: cart.state.subscription.id,
          title: cart.state.subscription.title,
          quantity: 1
        }
      ];

      cart.state.accessories.map((a) => {
        subscriptions.push({
          id: a.id,
          title: a.title,
          quantity: 1
        });
      });

      const payload = {
        ...data,
        subscriptions,
        subscriptionsId,
        bikeTypeId: cart.state.bike.bike.id,
        userId
      };

      const url = customerHasValidCreditCard
        ? "/api/sykkelabonnement/valid-card-order" // Save customer order
        : `/api/sykkelabonnement/order`; // Fetch client secret for stripe credit card form

      const response = await postData(url, payload);
      const { clientSecret } = response || {};

      if (clientSecret) {
        setStripeFormClientSecret(clientSecret);
        onFormSubmit(true); // Show credit card form
      }
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;

    updateForm({ key: name, value });
  };

  return (
    <Block top={6}>
      {/*@ts-ignore*/}
      <form onChange={onChange} onSubmit={handleSubmit(onSubmit)}>
        <div className={containerStyles.container}>
          <Block bottom={4}>
            <h3>Bestillingsskjema</h3>
          </Block>
          <InputHoneypot register={register} />

          <Input
            name="firstName"
            value={cart.state.form.firstName}
            label="Fornavn"
            register={register({
              required: "Skriv ditt fornavn"
            })}
            defaultValue={`${initialValues?.firstName}`}
            error={errors.firstName}
          />
          <Input
            name="lastName"
            value={cart.state.form.lastName}
            label="Etternavn"
            register={register({
              required: "Skriv ditt etternavn"
            })}
            defaultValue={`${initialValues?.lastName}`}
            error={errors.lastName}
          />

          <Input
            name="email"
            label="E-post"
            type="email"
            value={cart.state.form.email}
            register={register({
              required: "Skriv en gyldig e-post adresse",
              pattern: {
                value: /^\S+@\S+\.\S+$/,
                message: "Skriv inn en gyldig e-post adresse"
              }
            })}
            defaultValue={initialValues?.email}
            error={errors.email}
          />

          <Input
            name="phoneNumber"
            value={cart.state.form.phoneNumber}
            type="tel"
            label="Mobilnummer"
            widthCharacters="12"
            maxLength="8"
            register={register({
              required: "Skriv ditt mobilnummer, 8 siffer",
              pattern: {
                value: /^[0-9]*$/,
                message: "Skriv ditt mobilnummer, 8 siffer"
              },
              minLength: {
                value: 8,
                message: "Skriv ditt mobilnummer, 8 siffer"
              }
            })}
            defaultValue={initialValues?.phoneNumber}
            error={errors.phoneNumber}
          />

          <Input
            name="address"
            value={cart.state.form.address}
            label="Adresse"
            register={register({
              required: "Skriv din gateadresse"
            })}
            error={errors.address}
          />

          <div className={styles.zipWrap}>
            <Input
              name="zipCode"
              value={cart.state.form.zipCode}
              type="tel"
              label="Postnummer"
              maxLength="4"
              widthCharacters="4"
              register={register({
                required: "Skriv ditt postnummer, 4 siffer",
                pattern: {
                  value: /^[0-9]*$/,
                  message: "Skriv ditt postnummer, 4 siffer"
                },
                minLength: {
                  value: 4,
                  message: "Skriv ditt postnummer, 4 siffer"
                }
              })}
              error={errors.zipCode}
            />

            {city && <div className={styles.city}>{city}</div>}

            <Input
              name="city"
              label="Sted"
              register={register()}
              defaultValue={city}
              hidden
              value=""
            />
          </div>

          <Textarea
            name="message"
            value={cart.state.form.message}
            label="Er det noe annet vi bør vite?"
            placeholder="Skriv her om det er noe du vil fortelle oss."
            register={register()}
            defaultValue={initialValues?.message}
          />

          <Block top={2}>
            <Checkbox
              error={errors.checkbox}
              name="checkbox"
              register={register({
                required: "Du må huke av for å fortsette"
              })}
            >
              Jeg har lest, forstått og samtykker i Whee!{" "}
              <a href="/personvern-og-cookies" className="link">
                Personvern
              </a>
              {" og "}
              <a href="/kundeavtale-sykkelabonnement" className="link">
                kundeavtale for sykkelabonnement
              </a>
            </Checkbox>
          </Block>

          <Block top={6}>
            {errorMessage && <DefaultErrorMessage errorMessage={errorMessage} />}
          </Block>
        </div>
        <Block top={8} bottom={2}>
          <Flex justify="end">
            <Block right={4}>
              <Button onClick={prevStep} type="submit" secondary>
                Tilbake
              </Button>
            </Block>

            <Button
              type="submit"
              primary
              disabled={formState.isSubmitting}
              isSubmitting={formState.isSubmitting}
              errors={errors}
            >
              {customerHasValidCreditCard ? "Bestill nå" : "Neste"}
            </Button>
          </Flex>
        </Block>
      </form>
    </Block>
  );
};

export default OrderSykkelabo;
